import { useMemo } from 'react';
import type { DailyUsageData } from '../../utils/openai-api';
import { DateRange } from '../../constants/ui';
import { getChartReferenceAreas } from '../../utils/holidays';
import type { TimezoneMode } from '../../utils/storage';
import { isDailyAggregationRange, MOVING_AVERAGE_WINDOW_SIZE, SINGLE_POINT_OFFSET } from './constants';
import { formatDayKey, getDayStartTimestamp, safeParseDate, formatTimestampForDisplay } from './utils';
import type { RequestDataPoint, CostDataPoint, ApiKeyDataPoint, ModelDistributionPoint, ChartReferenceArea } from './types';

interface UseChartDataInput {
  chartData?: DailyUsageData[];
  dailyData: DailyUsageData[];
  dateRange: DateRange;
  currency: string;
  exchangeRate: number;
  timezoneMode: TimezoneMode;
}

/** Compute the timezone label string shown in tooltips */
const getTimezoneLabel = (mode: TimezoneMode): string => {
  if (mode === 'utc') return 'UTC';
  const offsetMin = new Date().getTimezoneOffset();
  const sign = offsetMin <= 0 ? '+' : '-';
  const absMin = Math.abs(offsetMin);
  const h = Math.floor(absMin / 60);
  const m = absMin % 60;
  return `UTC${sign}${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
};

interface UseChartDataOutput {
  chartSeries: DailyUsageData[];
  requestsData: RequestDataPoint[];
  costData: CostDataPoint[];
  apiKeyData: ApiKeyDataPoint[];
  uniqueModels: string[];
  modelDistributionData: ModelDistributionPoint[];
  showCostMovingAverage: boolean;
  referenceAreas: ChartReferenceArea[];
}

export function useChartData({ chartData, dailyData, dateRange, timezoneMode }: UseChartDataInput): UseChartDataOutput {
  const chartSeries = useMemo(() => {
    if (chartData && chartData.length > 0) {
      return chartData;
    }
    return dailyData;
  }, [chartData, dailyData]);

  const referenceAreas = useMemo(() => {
    return getChartReferenceAreas(chartSeries);
  }, [chartSeries]);

  const timezoneLabel = useMemo(() => getTimezoneLabel(timezoneMode), [timezoneMode]);

  // Prepare request data with timestamp conversion and optional daily aggregation
  const requestsData = useMemo((): RequestDataPoint[] => {
    if (!chartSeries || chartSeries.length === 0) return [];

    const dataWithTimestamp = chartSeries
      .map(item => {
        const openaiRequests = item.breakdowns?.openai?.requests ?? (item.provider === 'openai' ? item.requests ?? 0 : 0);
        const anthropicRequests = item.breakdowns?.anthropic?.requests ?? (item.provider === 'anthropic' ? item.requests ?? 0 : 0);
        const totalRequests = (openaiRequests + anthropicRequests) || item.requests || 0;
        const anthropicEstimated = item.breakdowns?.anthropic?.isEstimatedRequests ?? (item.provider === 'anthropic' ? item.isEstimatedRequests : false);
        const ts = safeParseDate(item.date);
        return {
          ...item,
          timestamp: ts,
          formattedDate: Number.isFinite(ts) ? formatTimestampForDisplay(ts, timezoneMode) : item.date,
          totalRequests,
          openaiRequests,
          anthropicRequests,
          anthropicEstimated: anthropicEstimated ?? false,
          timezoneLabel
        };
      })
      .filter(item => Number.isFinite(item.timestamp))
      .sort((a, b) => a.timestamp - b.timestamp);

    if (dataWithTimestamp.length === 1) {
      const singleDay = dataWithTimestamp[0];
      return [
        singleDay,
        {
          ...singleDay,
          timestamp: singleDay.timestamp + SINGLE_POINT_OFFSET
        }
      ];
    }

    if (!isDailyAggregationRange(dateRange)) {
      return dataWithTimestamp;
    }

    const aggregated = new Map<number, typeof dataWithTimestamp[number]>();

    dataWithTimestamp.forEach(point => {
      const dayStart = getDayStartTimestamp(point.timestamp);
      const existing = aggregated.get(dayStart);
      if (!existing) {
        const dayKey = formatDayKey(dayStart);
        aggregated.set(dayStart, {
          ...point,
          timestamp: dayStart,
          date: dayKey,
          formattedDate: dayKey,
          requests: point.requests ?? 0,
          totalRequests: point.totalRequests ?? 0,
          openaiRequests: point.openaiRequests ?? 0,
          anthropicRequests: point.anthropicRequests ?? 0,
          cost: point.cost ?? 0,
          anthropicEstimated: point.anthropicEstimated ?? false,
          timezoneLabel
        });
        return;
      }
      existing.requests = (existing.requests ?? 0) + (point.requests ?? 0);
      existing.totalRequests = (existing.totalRequests ?? 0) + (point.totalRequests ?? 0);
      existing.openaiRequests = (existing.openaiRequests ?? 0) + (point.openaiRequests ?? 0);
      existing.anthropicRequests = (existing.anthropicRequests ?? 0) + (point.anthropicRequests ?? 0);
      existing.cost = (existing.cost ?? 0) + (point.cost ?? 0);
      existing.anthropicEstimated = existing.anthropicEstimated || point.anthropicEstimated;
    });

    return Array.from(aggregated.values()).sort((a, b) => a.timestamp - b.timestamp);
  }, [chartSeries, dateRange, timezoneMode, timezoneLabel]);

  // Prepare cost data with timestamp conversion, daily aggregation, and optional moving average
  const costData = useMemo((): CostDataPoint[] => {
    if (!chartSeries || chartSeries.length === 0) return [];

    const dataWithTimestamp = chartSeries
      .map(item => {
        const openaiCost = item.breakdowns?.openai?.cost ?? (item.provider === 'openai' ? item.cost ?? 0 : 0);
        const anthropicCost = item.breakdowns?.anthropic?.cost ?? (item.provider === 'anthropic' ? item.cost ?? 0 : 0);
        const totalCost = (openaiCost + anthropicCost) || item.cost || 0;
        const ts = safeParseDate(item.date);
        return {
          ...item,
          timestamp: ts,
          formattedDate: Number.isFinite(ts) ? formatTimestampForDisplay(ts, timezoneMode) : item.date,
          totalCost,
          openaiCost,
          anthropicCost,
          timezoneLabel
        };
      })
      .filter(item => Number.isFinite(item.timestamp))
      .sort((a, b) => a.timestamp - b.timestamp);

    if (dataWithTimestamp.length === 1) {
      const singleDay = dataWithTimestamp[0];
      return [
        singleDay,
        {
          ...singleDay,
          timestamp: singleDay.timestamp + SINGLE_POINT_OFFSET
        }
      ];
    }

    if (!isDailyAggregationRange(dateRange)) {
      return dataWithTimestamp;
    }

    const aggregated = new Map<number, typeof dataWithTimestamp[number]>();

    dataWithTimestamp.forEach(point => {
      const dayStart = getDayStartTimestamp(point.timestamp);
      const existing = aggregated.get(dayStart);
      if (!existing) {
        const dayKey = formatDayKey(dayStart);
        aggregated.set(dayStart, {
          ...point,
          timestamp: dayStart,
          date: dayKey,
          formattedDate: dayKey,
          cost: point.cost ?? 0,
          totalCost: point.totalCost ?? 0,
          openaiCost: point.openaiCost ?? 0,
          anthropicCost: point.anthropicCost ?? 0,
          timezoneLabel
        });
        return;
      }
      existing.cost = (existing.cost ?? 0) + (point.cost ?? 0);
      existing.totalCost = (existing.totalCost ?? 0) + (point.totalCost ?? 0);
      existing.openaiCost = (existing.openaiCost ?? 0) + (point.openaiCost ?? 0);
      existing.anthropicCost = (existing.anthropicCost ?? 0) + (point.anthropicCost ?? 0);
    });

    const dailySeries = Array.from(aggregated.values()).sort((a, b) => a.timestamp - b.timestamp);

    if (dateRange === '30days' || dateRange === '60days' || dateRange === '90days' || dateRange === '120days' || dateRange === '150days' || dateRange === '180days' || dateRange === '360days') {
      const windowSize = MOVING_AVERAGE_WINDOW_SIZE;
      return dailySeries.map((point, index) => {
        if (index < windowSize - 1) {
          return {
            ...point,
            totalCostMA14: null
          };
        }
        const window = dailySeries.slice(index - windowSize + 1, index + 1);
        const totalCostSum = window.reduce((sum, item) => sum + (item.totalCost ?? 0), 0);
        const averageTotalCost = totalCostSum / window.length;
        return {
          ...point,
          totalCostMA14: averageTotalCost
        };
      });
    }

    return dailySeries;
  }, [chartSeries, dateRange, timezoneMode, timezoneLabel]);

  const showCostMovingAverage = dateRange === '30days' || dateRange === '60days' || dateRange === '90days' || dateRange === '120days' || dateRange === '150days' || dateRange === '180days' || dateRange === '360days';

  // Prepare API key data with timestamp conversion and optional daily aggregation
  const apiKeyData = useMemo((): ApiKeyDataPoint[] => {
    if (!chartSeries || chartSeries.length === 0) return [];

    const dataWithTimestamp = chartSeries
      .map(item => {
        const ts = safeParseDate(item.date);
        return {
          ...item,
          timestamp: ts,
          formattedDate: Number.isFinite(ts) ? formatTimestampForDisplay(ts, timezoneMode) : item.date,
          uniqueApiKeys: Number(item.uniqueApiKeys) || 0,
          timezoneLabel
        };
      })
      .filter(item => Number.isFinite(item.timestamp))
      .sort((a, b) => a.timestamp - b.timestamp);

    if (dataWithTimestamp.length === 1) {
      const singleDay = dataWithTimestamp[0];
      return [
        singleDay,
        {
          ...singleDay,
          timestamp: singleDay.timestamp + SINGLE_POINT_OFFSET
        }
      ];
    }

    if (!isDailyAggregationRange(dateRange)) {
      return dataWithTimestamp;
    }

    const aggregated = new Map<number, {
      timestamp: number;
      date: string;
      formattedDate: string;
      uniqueApiKeys: number;
      apiKeys: Set<string>;
      timezoneLabel: string;
    }>();

    dataWithTimestamp.forEach(point => {
      const dayStart = getDayStartTimestamp(point.timestamp);
      const keySet = point.apiKeys instanceof Set
        ? point.apiKeys
        : Array.isArray(point.apiKeys)
          ? new Set<string>(point.apiKeys)
          : new Set<string>();
      const existing = aggregated.get(dayStart);
      if (!existing) {
        const dayKey = formatDayKey(dayStart);
        aggregated.set(dayStart, {
          timestamp: dayStart,
          date: dayKey,
          formattedDate: dayKey,
          uniqueApiKeys: Math.max(keySet.size, point.uniqueApiKeys ?? 0),
          apiKeys: new Set<string>(keySet),
          timezoneLabel
        });
        return;
      }
      keySet.forEach(apiKey => existing.apiKeys.add(apiKey));
      existing.uniqueApiKeys = Math.max(
        existing.uniqueApiKeys,
        keySet.size,
        point.uniqueApiKeys ?? 0
      );
    });

    return Array.from(aggregated.values()).map(entry => ({
      ...entry,
      uniqueApiKeys: entry.apiKeys.size > 0 ? entry.apiKeys.size : entry.uniqueApiKeys
    })).sort((a, b) => a.timestamp - b.timestamp);
  }, [chartSeries, dateRange, timezoneMode, timezoneLabel]);

  // Get unique models for the chart
  const uniqueModels = useMemo(() => {
    const models = new Set<string>();
    dailyData.forEach(day => {
      Object.keys(day.models || {}).forEach(model => models.add(model));
    });
    return Array.from(models).sort();
  }, [dailyData]);

  // Calculate model distribution data
  const modelDistributionData = useMemo((): ModelDistributionPoint[] => {
    if (!dailyData || dailyData.length === 0) return [];

    let sourceData: Array<{
      date: string;
      requests?: number;
      models: Record<string, number>;
    }> = dailyData;

    if (isDailyAggregationRange(dateRange)) {
      const needsAggregation = dailyData.some(day => typeof day.date === 'string' && day.date.includes(':'));
      if (needsAggregation) {
        const aggregated = new Map<string, { date: string; requests: number; models: Record<string, number> }>();

        dailyData.forEach(day => {
          const dateValue = day.date;
          const timestamp = typeof dateValue === 'string'
            ? safeParseDate(dateValue)
            : Number(dateValue);
          if (!Number.isFinite(timestamp)) {
            return;
          }
          const dayKey = formatDayKey(timestamp);
          let entry = aggregated.get(dayKey);
          if (!entry) {
            entry = { date: dayKey, requests: 0, models: {} };
            aggregated.set(dayKey, entry);
          }
          entry.requests += day.requests ?? 0;
          Object.entries(day.models || {}).forEach(([modelName, value]) => {
            const numeric = typeof value === 'number' ? value : Number(value);
            if (Number.isFinite(numeric) && numeric !== 0) {
              entry.models[modelName] = (entry.models[modelName] ?? 0) + numeric;
            }
          });
        });

        const aggregatedArray = Array.from(aggregated.values()).sort((a, b) => a.date.localeCompare(b.date));
        if (aggregatedArray.length > 0) {
          sourceData = aggregatedArray;
        }
      }
    }

    // For single data point, handle appropriately
    if (sourceData.length === 1) {
      const singleDay = sourceData[0];
      const modelCounts = singleDay.models || {};
      const totalFromModels = Object.values(modelCounts).reduce((sum: number, count: number) => sum + (count || 0), 0);
      const totalRequests = totalFromModels > 0 ? totalFromModels : (singleDay.requests || 1);
      const modelPercentages: Record<string, number> = {};

      uniqueModels.forEach(model => {
        modelPercentages[model] = 0;
      });

      Object.entries(modelCounts).forEach(([model, count]) => {
        modelPercentages[model] = Math.round((count / totalRequests) * 10000) / 100;
      });

      if (singleDay.date.includes(':')) {
        const ts = safeParseDate(singleDay.date);
        return [{
          date: singleDay.date,
          formattedDate: Number.isFinite(ts) ? formatTimestampForDisplay(ts, timezoneMode) : singleDay.date,
          timezoneLabel,
          ...modelPercentages
        }];
      }

      return [
        {
          date: singleDay.date + ' 00:00',
          formattedDate: singleDay.date + ' 00:00',
          timezoneLabel,
          ...modelPercentages
        },
        {
          date: singleDay.date + ' 23:59',
          formattedDate: singleDay.date + ' 23:59',
          timezoneLabel,
          ...modelPercentages
        }
      ];
    }

    return sourceData.map(day => {
      const modelCounts = day.models || {};
      const totalFromModels = Object.values(modelCounts).reduce((sum: number, count: number) => sum + (count || 0), 0);
      const totalRequests = totalFromModels > 0 ? totalFromModels : (day.requests || 1);
      const modelPercentages: Record<string, number> = {};

      uniqueModels.forEach(model => {
        modelPercentages[model] = 0;
      });

      Object.entries(modelCounts).forEach(([model, count]) => {
        modelPercentages[model] = Math.round((count / totalRequests) * 10000) / 100;
      });

      const ts = day.date.includes(':') ? safeParseDate(day.date) : NaN;
      return {
        date: day.date,
        formattedDate: Number.isFinite(ts) ? formatTimestampForDisplay(ts, timezoneMode) : day.date,
        timezoneLabel,
        ...modelPercentages
      };
    });
  }, [dailyData, uniqueModels, dateRange, timezoneMode, timezoneLabel]);

  return {
    chartSeries,
    requestsData,
    costData,
    apiKeyData,
    uniqueModels,
    modelDistributionData,
    showCostMovingAverage,
    referenceAreas
  };
}
